import { useParams, useLocation, Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { fetchProductsFromSheet } from "../utils/sheetProducts";
import { getVariantsForDetail } from "../utils/groupProducts";
import { buildWeightLabel } from "../utils/formatWeight";
import ProductReviews from "../components/ProductReviews";
import ProductMediaGallery from "../components/ProductMediaGallery";
import ProductVariantPicker from "../components/ProductVariantPicker";
import { ProductPriceBlock } from "../components/ProductPriceBlock";
import ProductGiftTray from "../components/ProductGiftTray";
import ProductAssemblyCatalogue from "../components/ProductAssemblyCatalogue";
import ScrollElevate from "../components/ScrollElevate";
import { BtnContent } from "../components/BtnIcon";
import { IoCartOutline, IoChatbubbleEllipsesOutline, IoStorefrontOutline, IoCheckmarkCircle } from "react-icons/io5";
import "../styles/product-gift-tray.css";
import "../styles/scroll-elevate.css";
import "../styles/product-variant-picker.css";

function ProductDetails({ addToCart }) {
  const { id } = useParams();
  const location = useLocation();

  const [product, setProduct] = useState(location.state?.product || null);
  const [catalog, setCatalog] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [added, setAdded] = useState(false);
  const [groupBy, setGroupBy] = useState(location.state?.groupBy || "none");

  useEffect(() => {
    let cancelled = false;
    fetchProductsFromSheet()
      .then((rows) => {
        if (!cancelled) setCatalog(rows);
      })
      .catch(() => {
        if (!cancelled) setCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (product || !id) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchProductsFromSheet();
        if (cancelled) return;
        const found = list.find((p) => String(p.id) === String(id));
        if (found) setProduct(found);
        else setLoadError("This product could not be found.");
      } catch {
        if (!cancelled) setLoadError("Could not load product. Check your connection and try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, product]);

  useEffect(() => {
    const fromState = location.state?.product;
    if (fromState && String(fromState.id) === String(id)) {
      setProduct(fromState);
      setLoadError(null);
    }
    if (location.state?.groupBy) {
      setGroupBy(location.state.groupBy);
    }
  }, [location.state, id]);

  const variants = useMemo(() => {
    if (location.state?.variants?.length) return location.state.variants;
    if (!product || !catalog.length) return product ? [product] : [];
    const mode = location.state?.groupBy || groupBy;
    return getVariantsForDetail(product, catalog, mode);
  }, [location.state?.variants, location.state?.groupBy, product, catalog, groupBy]);

  const resolvedGroupBy = useMemo(() => {
    if (groupBy && groupBy !== "none") return groupBy;
    if (variants.length <= 1) return "none";
    const colorCount = new Set(variants.map((v) => v.color).filter(Boolean)).size;
    const sizeCount = new Set(variants.map((v) => v.boxSizeLabel || v.fabric).filter(Boolean)).size;
    if (colorCount > 1 && sizeCount > 1) return "none";
    if (sizeCount > 1) return "color";
    if (colorCount > 1) return "size";
    return "none";
  }, [groupBy, variants]);

  useEffect(() => {
    if (!location.state?.justLoggedIn || !location.state?.openReview) return;
    const el = document.getElementById("product-reviews");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.state, product]);

  if (loadError) {
    return (
      <div className="container text-center py-4 product-page-detail">
        <p className="text-muted mb-3">{loadError}</p>
        <Link to="/shop" className="btn btn-primary">
          <BtnContent icon={IoStorefrontOutline}>Back to shop</BtnContent>
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container text-center py-4 product-page-detail">
        <p className="text-muted">Loading product…</p>
      </div>
    );
  }

  const handleAdd = () => {
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleVariantSelect = (next) => {
    setProduct(next);
    setAdded(false);
  };

  const showColorLine = resolvedGroupBy !== "color";
  const showSizeLine = resolvedGroupBy !== "size";

  return (
    <div className="container product-page-detail">
      <p className="small mb-2 product-page-detail__crumb">
        <Link to="/shop" className="text-muted">
          ← Shop
        </Link>
        <span className="text-muted mx-2">·</span>
        <Link to="/gift-guide" className="text-muted">
          Gift guide
        </Link>
      </p>
      <ScrollElevate className="product-page-detail__panel-wrap">
        <div className="row product-page-detail__panel">
          <div className="col-md-6">
            <ProductMediaGallery product={product} />
          </div>

          <div className="col-md-6">
            <h1 className="mb-3">{product.name}</h1>

            <div className="mb-3">
              <ProductPriceBlock product={product} variant="product" />
            </div>

            <p className="mb-3">{product.description}</p>

            <ProductVariantPicker
              variants={variants}
              product={product}
              groupBy={resolvedGroupBy}
              onSelect={handleVariantSelect}
            />

            <div className="mb-3 small">
              <p className="mb-1">
                <b>Category:</b> {product.category || product.fabric}
              </p>
              {showSizeLine && product.boxSizeLabel ? (
                <p className="mb-1">
                  <b>Box size (assembled):</b> {product.boxSizeLabel}
                </p>
              ) : null}
              {product.weight ? (
                <p className="mb-1">
                  <b>Weight:</b> {buildWeightLabel(product.weight)}
                </p>
              ) : null}
              {showColorLine ? (
                <p className="mb-1">
                  <b>Color:</b> {product.color}
                </p>
              ) : null}
              {product.boxGroup ? (
                <p className="mb-1 text-muted">
                  <b>Collection:</b> {product.boxGroup}
                </p>
              ) : null}
              {product.boxId || product.saree_id ? (
                <p className="mb-0 text-muted">
                  <b>Box ID:</b> {product.boxId || product.saree_id}
                </p>
              ) : null}
            </div>

            <button className="btn btn-dark px-4" onClick={handleAdd} disabled={product.stock === 0}>
              {product.stock === 0 ? (
                "Out of stock"
              ) : added ? (
                <BtnContent icon={IoCheckmarkCircle}>Added to cart</BtnContent>
              ) : (
                <BtnContent icon={IoCartOutline}>Add to cart</BtnContent>
              )}
            </button>
            <Link
              to={`/customise/${product.id}`}
              state={{ product }}
              className="btn btn-outline-dark px-4 ms-0 ms-sm-2 mt-2 mt-sm-0 d-inline-block"
            >
              <BtnContent icon={IoChatbubbleEllipsesOutline}>Message on gift box</BtnContent>
            </Link>
          </div>
        </div>
      </ScrollElevate>

      <ScrollElevate delay={0.08}>
        <ProductAssemblyCatalogue />
      </ScrollElevate>

      <ScrollElevate delay={0.12}>
        <ProductGiftTray currentProductId={product.id} products={catalog} addToCart={addToCart} />
      </ScrollElevate>

      <ScrollElevate delay={0.06}>
        <ProductReviews productId={String(product.id)} />
      </ScrollElevate>
    </div>
  );
}

export default ProductDetails;
